import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Pipette } from 'lucide-react';
import { PROVIDER_COLORS, readableTextColor } from '@nova/shared';
import { cn } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

export interface ColorPickerProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange' | 'defaultValue'> {
  /** Hex, `#rrggbb`. */
  value?: string;
  onChange?: (hex: string) => void;
  label?: string;
  /** Palette to offer; defaults to the provider palette (DS-53). */
  colors?: readonly string[];
  disabled?: boolean;
  /** Hide the `#rrggbb` readout under the grid. */
  showHex?: boolean;
  customLabel?: string;
  /**
   * `inline` lays the whole palette out at once — the default, and what a form
   * with room for it wants. `dropdown` keeps it behind one swatch-sized trigger,
   * for table rows where ten swatches per line drown the fields next to them.
   */
  variant?: 'inline' | 'dropdown';
  /** Dropdown trigger accessible name. */
  triggerLabel?: string;
}

const normalize = (hex: string | undefined) => (hex ?? '').trim().toLowerCase();

/**
 * PROV-01 provider colours, APT-05 appointment tags, INV-23 category nodes and
 * APT-17 calendar notes all pick from the same palette, so the calendar stays
 * readable no matter which screen created the colour. Anything outside the
 * palette goes through the native picker and lands on the custom swatch.
 */
export const ColorPicker = React.forwardRef<HTMLDivElement, ColorPickerProps>(function ColorPicker(
  {
    value,
    onChange,
    label,
    colors = PROVIDER_COLORS,
    disabled = false,
    showHex = true,
    customLabel: customLabelProp,
    variant = 'inline',
    triggerLabel: triggerLabelProp,
    className,
    ...props
  },
  ref,
) {
  const { t } = useTranslation('common');
  const customLabel = customLabelProp ?? t('ui.colorCustom');
  const triggerLabel = triggerLabelProp ?? t('fields.color');
  const [open, setOpen] = React.useState(false);
  const selected = normalize(value);
  const isCustom = selected.length > 0 && !colors.some((c) => normalize(c) === selected);
  // The native input needs a valid hex at all times or Chrome silently resets it.
  const customValue = isCustom ? selected : '#000000';

  const swatch = (color: string) => {
    const isSelected = normalize(color) === selected;
    return (
      <button
        key={color}
        type="button"
        disabled={disabled}
        aria-label={color}
        aria-pressed={isSelected}
        onClick={() => {
          onChange?.(color);
          setOpen(false);
        }}
        style={{ backgroundColor: color }}
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded border border-black/10 transition',
          'focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
          isSelected ? 'ring-1 ring-slate-800 ring-offset-1' : 'hover:scale-110',
          disabled && 'cursor-not-allowed opacity-50 hover:scale-100',
        )}
      >
        {isSelected ? (
          <Check className="h-3 w-3" strokeWidth={3.5} style={{ color: readableTextColor(color) }} />
        ) : null}
      </button>
    );
  };

  const customSwatch = (
    <span
      className={cn(
        'relative flex h-5 w-5 items-center justify-center rounded border border-slate-300 transition',
        isCustom ? 'ring-1 ring-slate-800 ring-offset-1' : 'bg-white',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-slate-400',
      )}
      style={isCustom ? { backgroundColor: customValue } : undefined}
      title={customLabel}
    >
      <Pipette
        className="pointer-events-none h-3 w-3"
        style={{ color: isCustom ? readableTextColor(customValue) : '#64748b' }}
      />
      <input
        type="color"
        aria-label={customLabel}
        disabled={disabled}
        value={customValue}
        onChange={(event) => onChange?.(event.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
    </span>
  );

  const hexReadout = (
    <span className="ltr-num font-mono text-[10px] uppercase text-slate-500">{selected || '—'}</span>
  );

  if (variant === 'dropdown') {
    return (
      <div ref={ref} className={cn('flex flex-col gap-1.5', className)} {...props}>
        {label ? <span className="label-micro">{label}</span> : null}

        <Popover.Root open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
          <Popover.Trigger
            type="button"
            disabled={disabled}
            aria-label={triggerLabel}
            title={triggerLabel}
            className={cn(
              'flex items-center gap-1.5 rounded border border-slate-300 bg-white px-1.5 py-1 transition',
              'focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500',
              disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-slate-400',
            )}
          >
            <span
              className={cn('h-4 w-4 shrink-0 rounded border border-black/10', !selected && 'bg-white')}
              style={selected ? { backgroundColor: selected } : undefined}
              aria-hidden
            />
            {showHex ? hexReadout : null}
            <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              dir="rtl"
              align="start"
              sideOffset={4}
              className={cn(
                'z-50 rounded border border-slate-300 bg-white p-2 shadow-lg',
                'data-[state=open]:animate-zoomIn',
              )}
            >
              <div className="grid grid-cols-5 gap-1.5">{colors.map(swatch)}</div>

              <div className="mt-2 flex items-center gap-1.5 border-t border-slate-200 pt-2">
                {customSwatch}
                <span className="text-[10px] text-slate-500">{customLabel}</span>
                <span className="ms-auto">{hexReadout}</span>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    );
  }

  return (
    <div ref={ref} className={cn('flex flex-col gap-1.5', className)} {...props}>
      {label ? <span className="label-micro">{label}</span> : null}

      <div className="flex flex-wrap items-center gap-1.5">
        {colors.map(swatch)}

        <span className="mx-0.5 h-5 w-px bg-slate-200" aria-hidden="true" />

        {customSwatch}

        {showHex ? <span className="ms-1">{hexReadout}</span> : null}
      </div>
    </div>
  );
});
